import * as React from 'react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { FormInput } from '@enginuity-apps-ui-components/molecules/form-elements/form-input/form-input';
import { Button } from '@enginuity-apps-ui-components/atoms/button/button';
import { FormCheckbox } from '@enginuity-apps-ui-components/molecules/form-elements/form-checkbox/form-checkbox';
import { ErrorList } from '@enginuity-apps-ui-components/molecules/form-elements/error-list/error-list';

export type IRegistrationFormData = {
  firstName?: string;
  lastName?: string;
  email?: string;
  terms?: boolean;
  keepMeInformed?: boolean;
};

export type IRegisterFormProps = {
  onSubmit: (data: IRegistrationFormData) => void;
  fromUri?: string;
  data?: IRegistrationFormData;
};

export default function RegisterForm({ onSubmit, fromUri, data }: IRegisterFormProps) {
  const { register, handleSubmit, errors } = useForm();

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="row md-spacer-32 spacer-64">
        <div className="col-lg-2"></div>
        <div className="col-lg-10">
          <h3 className="color-malachite-green">01. Account setup</h3>
        </div>
      </div>
      <div className="row md-spacer-32 spacer-16">
        <div className="col-lg-2"></div>
        <div className="col-lg-4 col-xl-3">
          <FormInput
            label={<>First Name *</>}
            id="firstName"
            defaultValue={data && data.firstName}
            ref={register({
              required: "Please enter your first name.", maxLength: {
                value: 50, message: 'Last name cannot be more than 50 characters'
              }
            })}
            errors={errors}
          />
        </div>
        <div className="col-lg-4 col-xl-3  lg-no-spacer spacer-16">
          <FormInput
            label={<>Last Name *</>}
            id="lastName"
            defaultValue={data && data.lastName}
            ref={register({
              required: "Please enter your last name.", maxLength: {
                value: 50, message: 'Last name cannot be more than 50 characters'
              }
            })}
            errors={errors}
          />
        </div>
      </div>
      <div className="row spacer-16">
        <div className="col-lg-2"></div>
        <div className="col-lg-4 col-xl-3">
          <FormInput
            label={<>Email *</>}
            id="email"
            defaultValue={data && data.email}
            ref={register({
              required: "Please enter your email for the account.", minLength: {
                value: 5, message: 'Email length cannot be less than 5 characters'
              }, maxLength: {
                value: 100, message: 'Email length cannot be more than 100 characters'
              },
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                message: "Please enter a valid email address"
              }
            })}
            errors={errors}
          />
        </div>
      </div>
      <div className="row spacer-16">
        <div className="col-lg-2"></div>
        <div className="col-lg-10">
          <p className="required-field-text color-purple-grey">* indicates required field</p>
        </div>
      </div>
      <div className="row md-spacer-32 spacer-72">
        <div className="col-lg-2"></div>
        <div className="col-lg-10">
          <h3 className="color-malachite-green">02. Terms</h3>
        </div>
      </div>
      <div className="row spacer-32">
        <div className="col-lg-2"></div>
        <div className="col-lg-10">
          <p>In order to create an Enginuity Account you will need to confirm the following:</p>
        </div>
      </div>
      <div className="row spacer-16">
        <div className="col-lg-2"></div>
        <div className="col-lg-10">
          <FormCheckbox
            label={<>I’ve read the&nbsp;<a href='https://enginuity.org/privacy/' target="_blank">Privacy Policy</a>&nbsp;&amp;&nbsp;I accept the&nbsp;<a href='https://enginuity.org/engage-terms/' target="_blank">Terms &amp; Conditions</a></>}
            id="terms"
            defaultChecked={data && data.terms}
            ref={register({ required: "Please confirm you are happy with our Privacy Policy and Terms & Conditions" })}
            errors={errors}
          />
        </div>
      </div>
      <div className="row spacer-32">
        <div className="col-lg-2"></div>
        <div className="col-lg-10">
          <p>Marketing and future communication:</p>
        </div>
      </div>
      <div className="row spacer-16">
        <div className="col-lg-2"></div>
        <div className="col-lg-10">
          <FormCheckbox
            label={<div>
              <p>Get personalised emails based on your Engage interests and activity.<br /><br /></p>
              <p>We treat your data with the utmost care, and you can unsubscribe at any time.<br /><br /></p>
              <p>“We” includes Enginuity Group, its subsidiaries (Enginuity, Enginuity Enterprise, EAL), and technology partners (Filtered). <br /><br /></p>
              <p>For further details on how your data is used and stored: <a href="https://enginuity.org/privacy/" target="_blank">https://enginuity.org/privacy/</a></p>
            </div>}
            id="keepMeInformed"
            defaultChecked={data && data.keepMeInformed}
            ref={register()}
            errors={errors}
          />
        </div>
      </div>
      <div className="row spacer-32">
        <div className="col-lg-2"></div>
        <div className="col-lg-10">
          <ErrorList errors={errors}>We found some errors. Please review the form and make the corrections.</ErrorList>
        </div>
      </div>
      <div className="row spacer-32">
        <div className="col-lg-2"></div>
        <div className="col-lg-10">
          <Button buttonType="button" type="submit" className="cmp-button--primary">Create my account</Button>
        </div>
      </div>
      <div className="row spacer-32">
        <div className="col-lg-2"></div>
        <div className="col-lg-10">
          <a href={`/signin${fromUri ? `?fromURI=${fromUri}` : ''}`} className="link--secondary" tabIndex={0}>&lt;&nbsp;I already have an account</a>
        </div>
      </div>
      <div className="row spacer-32">
      </div>
    </form>
  );
}
